import { useParams } from "react-router-dom";
import {
  Examination_Type,
  Trial_File_Type,
  Trial_Status,
  TrialHeaderQuery,
  useCancelTrialMutation,
} from "@generated/graphql";
import { Checkbox, FormControlLabel, Stack, Tooltip, Typography } from "@mui/material";
import { useContext, useState } from "react";
import useIsCreate from "hooks/useIsCreate";
import TestingStatus from "components/TestingStatus";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import useSetTabTitle from "hooks/useSetTabTitle";
import { TESTING_TYPES_DATA } from "constants/global";
import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import useGenerateTrialFile from "hooks/useGenerateTrialFile";
import { parseApolloError } from "helpers/errorsParser";
import ExaminationTypeTitle from "components/ExaminationTypeTitle";
import { getTestingTitle } from "../../helpers";
import CancelExaminationButton from "../../components/CancelExaminationButton";

interface PersonalHeaderProps {
  trial: TrialHeaderQuery["trial"] | undefined;
}

const PersonalHeader = ({ trial }: PersonalHeaderProps) => {
  const { addToast } = useContext(ToastContext);
  const [isReportReduced, setIsReportReduced] = useState<boolean>(false);
  const isCreate = useIsCreate();
  const { id } = useParams();
  const status = trial?.status;
  const trialId = id || trial?.id;

  const isCanCancel = status === Trial_Status.Created;

  const [cancelTrial, { loading: cancelTrialLoading }] = useCancelTrialMutation();

  const handleCancelExamination = async () => {
    cancelTrial({
      variables: {
        trialId: id!,
      },
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS }))
      .catch((e) => {
        parseApolloError(e, { addToast });
      });
  };

  const numericId = trial?.examination.numericId;
  const title = getTestingTitle(trial?.examination);
  useSetTabTitle(title, numericId);

  const { onGenerateTrialFile, loading } = useGenerateTrialFile();

  const isDownloadReportDisabled = trial?.status !== Trial_Status.Completed && !trial?.isAssessmentComplete;

  const examinationTypeData = TESTING_TYPES_DATA.find((data) => data.type === Examination_Type.Personal);

  const downloadReportType: Trial_File_Type = isReportReduced
    ? Trial_File_Type.CandidateReport
    : Trial_File_Type.RecruiterReport;

  const handleGenerateTrialFile = () => {
    if (trialId) onGenerateTrialFile(trialId, downloadReportType);
    else addToast({ type: ToastTypeEnum.ERROR, text: "Отсутствует id тестирования" });
  };

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      mb={2.5}
      spacing={2}>
      <Stack
        direction='row'
        alignItems='flex-start'
        spacing={2}>
        <Typography
          variant='h28'
          sx={{ wordBreak: "normal" }}>
          {title}
        </Typography>
        <ExaminationTypeTitle examinationTypeData={examinationTypeData} />
      </Stack>
      <Stack
        direction='row'
        spacing={2}
        alignItems='flex-start'>
        {!isCreate && (
          <>
            {status && <TestingStatus status={status} />}
            <CancelExaminationButton
              buttonProps={{ disabled: !isCanCancel, loading: cancelTrialLoading }}
              handleCancelExamination={handleCancelExamination}
            />
          </>
        )}
        {!isCreate && (
          <Stack spacing={1}>
            <Tooltip title={isDownloadReportDisabled ? "Отчет еще не сформирован" : undefined}>
              <Box>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={loading}
                  disabled={isDownloadReportDisabled}
                  sx={{ gap: "6px", whiteSpace: "nowrap" }}
                  onClick={handleGenerateTrialFile}>
                  Выгрузить отчет
                </LoadingButton>
              </Box>
            </Tooltip>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isReportReduced}
                  onChange={(event) => setIsReportReduced(event.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={<Typography sx={{ whiteSpace: "nowrap" }}> Сокращенный отчет</Typography>}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PersonalHeader;
